import { useCallback, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { ErrorState } from '@/shared/components/ErrorState'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'

import {
  useCampaign,
  useCampaignInstruments,
} from '../api/campaign-queries'
import { computeLaunchBudgetUsed } from '../lib/launch-budget'
import {
  useReactivateInstrument,
  useUnlockLoi,
} from '../api/campaign-mutations'
import {
  useDeleteDraftInstrument,
  usePauseInstrument,
  useStopInstrument,
} from '../api/instrument-dashboard-mutations'
import {
  InsufficientBudgetModal,
  LaunchCampaignOverviewCard,
  LaunchLoiCard,
  LaunchMetricCards,
  DeleteDraftInstrumentDialog,
  PauseInstrumentDialog,
  ReactivateInstrumentModal,
  StopInstrumentDialog,
  SendSurveyReminderDialog,
  CampaignAnalyticsPlaceholder,
} from '../components'
import { useReactivateBudgetGate } from '../hooks/useReactivateBudgetGate'
import {
  instrumentCreatePath,
  instrumentInfoContinuePath,
} from '@/features/instruments/lib/instrumentNavigation'
import { campaignWizardResumePath } from '../lib/campaignResumeWizardPath'
import { hasCampaignEndDatePassed } from '../lib/campaign-end-date'
import { loiDashboardPath } from '../lib/loiDashboardNavigation'
import { instrumentDashboardPath } from '../lib/instrumentDashboardNavigation'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'
import { useLiveEditEntry } from '../hooks/useLiveEditEntry'
import type { CampaignInstrument, LineOfInquiry } from '../types'

const POLL_MS = 15_000

/** Same content cap as the campaign wizard so the launch table can show every action icon. */
const LAUNCH_SCREEN_CONTENT_MAX_WIDTH_PX = 1760

function sortLois(lois: LineOfInquiry[] | null | undefined): LineOfInquiry[] {
  if (!Array.isArray(lois) || lois.length === 0) return []
  return [...lois].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

export function CampaignDetailPage() {
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const role = useSessionStore((s) => s.user?.role)
  const { showToast } = useToast()

  const {
    data: campaign,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useCampaign(campaignId, { refetchIntervalMs: POLL_MS })

  const {
    data: instruments,
    isLoading: instrumentsLoading,
  } = useCampaignInstruments(campaignId, {
    refetchIntervalMs: POLL_MS,
    enabled: Boolean(campaign?.status === 'active'),
  })

  const unlockLoi = useUnlockLoi()
  const reactivateInstrument = useReactivateInstrument()
  const pauseInstrument = usePauseInstrument()
  const deleteDraftInstrument = useDeleteDraftInstrument()
  const stopInstrument = useStopInstrument()
  const budgetGate = useReactivateBudgetGate({
    onCheckFailed: (message) => showToast(message, 'error'),
  })

  const [unlockTargetLoiId, setUnlockTargetLoiId] = useState<string | null>(
    null,
  )
  const [reactivateTarget, setReactivateTarget] =
    useState<CampaignInstrument | null>(null)
  const [pauseTarget, setPauseTarget] = useState<CampaignInstrument | null>(null)
  const [deleteDraftTarget, setDeleteDraftTarget] = useState<CampaignInstrument | null>(null)
  const [stopTarget, setStopTarget] = useState<CampaignInstrument | null>(null)
  const [reminderTarget, setReminderTarget] = useState<CampaignInstrument | null>(null)

  useDocumentTitle(campaign?.name?.trim() ?? 'Campaign')

  const canWrite = role === 'ADMIN' || role === 'EDITOR'
  const { requestLiveEdit, liveEditDialog } = useLiveEditEntry()

  const sortedLois = useMemo(
    () => sortLois(campaign?.linesOfInquiry ?? null),
    [campaign?.linesOfInquiry],
  )

  const instrumentsByLoi = useMemo(() => {
    const m = new Map<string, CampaignInstrument[]>()
    for (const row of instruments ?? []) {
      if (!row.loiId) continue
      const list = m.get(row.loiId) ?? []
      list.push(row)
      m.set(row.loiId, list)
    }
    return m
  }, [instruments])

  const totalResponses = useMemo(
    () =>
      (instruments ?? []).reduce(
        (sum, row) => sum + (row.responseCount ?? 0),
        0,
      ),
    [instruments],
  )

  const budgetUsed = useMemo(
    () => computeLaunchBudgetUsed(instruments),
    [instruments],
  )

  const onCreateInstrument = useCallback(
    (loiId: string) => {
      navigate(instrumentCreatePath(campaignId, loiId))
    },
    [campaignId, navigate],
  )

  const onContinueInstrument = useCallback(
    (instrumentId: string, loiId: string) => {
      navigate(instrumentInfoContinuePath(campaignId, loiId, instrumentId))
    },
    [campaignId, navigate],
  )

  const onDashboard = useCallback(
    (instrumentId: string) => {
      navigate(instrumentDashboardPath(instrumentId))
    },
    [navigate],
  )

  const onLoiResponseDashboard = useCallback(
    (loiId: string) => {
      navigate(loiDashboardPath(loiId))
    },
    [navigate],
  )

  const confirmUnlock = async () => {
    if (!campaignId || !unlockTargetLoiId) return
    try {
      await unlockLoi.mutateAsync({
        campaignId,
        loiId: unlockTargetLoiId,
      })
      showToast('Line of inquiry unlocked', 'success')
      setUnlockTargetLoiId(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Unlock failed'), 'error')
    }
  }

  const confirmReactivate = async () => {
    if (!campaignId || !reactivateTarget?.loiId) return
    try {
      await reactivateInstrument.mutateAsync({
        campaignId,
        instrumentId: reactivateTarget.id,
        loiId: reactivateTarget.loiId,
      })
      showToast('Instrument reactivated', 'success')
      setReactivateTarget(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Reactivation failed'), 'error')
    }
  }

  const handleReactivateClick = (instrument: CampaignInstrument) => {
    void budgetGate.checkBudgetThen(instrument, () => setReactivateTarget(instrument))
  }

  const confirmDeleteDraft = async () => {
    if (!campaignId || !deleteDraftTarget?.loiId) return
    try {
      await deleteDraftInstrument.mutateAsync({
        campaignId,
        loiId: deleteDraftTarget.loiId,
        instrumentId: deleteDraftTarget.id,
      })
      showToast('Draft survey removed', 'success')
      setDeleteDraftTarget(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Could not remove draft survey'), 'error')
    }
  }

  const confirmPause = async () => {
    if (!campaignId || !pauseTarget?.loiId) return
    try {
      await pauseInstrument.mutateAsync({
        campaignId,
        loiId: pauseTarget.loiId,
        instrumentId: pauseTarget.id,
      })
      showToast('Instrument paused', 'success')
      setPauseTarget(null)
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Pause failed'), 'error')
    }
  }

  const confirmStop = async (body: {
    stopReason: string
    stopReasonOther?: string | null
  }) => {
    if (!campaignId || !stopTarget?.loiId) return
    try {
      await stopInstrument.mutateAsync({
        campaignId,
        loiId: stopTarget.loiId,
        instrumentId: stopTarget.id,
        stopReason: body.stopReason,
        stopReasonOther: body.stopReasonOther,
      })
      showToast('Instrument stopped', 'success')
      const stoppedId = stopTarget.id
      setStopTarget(null)
      navigate(instrumentDashboardPath(stoppedId))
    } catch (e: unknown) {
      showToast(messageFromUnknownApiError(e, 'Stop failed'), 'error')
    }
  }

  if (campaignId === 'new') {
    return <Navigate to="/campaigns/new/setup" replace />
  }

  const showInitialSkeleton =
    isLoading || (isFetching && !campaign)

  if (showInitialSkeleton) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: LAUNCH_SCREEN_CONTENT_MAX_WIDTH_PX },
          mx: 'auto',
          py: 2,
          bgcolor: '#F9FAFB',
        }}
        data-testid="launch-page-skeleton"
      >
        <Skeleton variant="rounded" height={220} sx={{ mb: 2, borderRadius: '10px' }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={88} sx={{ flex: 1, borderRadius: '10px' }} />
          <Skeleton variant="rounded" height={88} sx={{ flex: 1, borderRadius: '10px' }} />
          <Skeleton variant="rounded" height={88} sx={{ flex: 1, borderRadius: '10px' }} />
        </Stack>
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: '10px' }} />
      </Box>
    )
  }

  if (isError || !campaign) {
    return <ErrorState onRetry={refetch} />
  }

  if (searchParams.get('view') === 'analytics') {
    return (
      <CampaignAnalyticsPlaceholder
        campaignId={campaignId}
        campaignName={campaign.name?.trim() || 'Campaign'}
      />
    )
  }

  const wizardPath = campaignWizardResumePath(campaign)
  if (wizardPath) {
    return <Navigate to={wizardPath} replace />
  }

  const campaignEnded =
    campaign.status === 'completed' || hasCampaignEndDatePassed(campaign.endDate)

  return (
    <>
      <Box
        sx={{
          bgcolor: '#F9FAFB',
          width: '100%',
          maxWidth: { xs: '100%', md: LAUNCH_SCREEN_CONTENT_MAX_WIDTH_PX },
          mx: 'auto',
          pb: 4,
        }}
        data-testid="campaign-launching-screen"
      >
        <LaunchCampaignOverviewCard campaign={campaign} />
        <LaunchMetricCards
          budgetUsed={budgetUsed}
          totalResponses={totalResponses}
          campaignEnded={campaignEnded}
        />

        <Stack spacing={0.5} sx={{ mb: '32px' }}>
          <Typography
            component="h2"
            data-testid="launch-loi-section-heading"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              letterSpacing: '-0.4492px',
              color: '#0F1729',
            }}
          >
            Lines of Inquiry
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#4A5565',
            }}
          >
            Your generated lines of inquiries...
          </Typography>
        </Stack>

        {instrumentsLoading && !instruments ? (
          <Stack spacing={1} data-testid="launch-instruments-skeleton">
            <Skeleton variant="rounded" height={120} />
            <Skeleton variant="rounded" height={120} />
          </Stack>
        ) : null}

        {sortedLois.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No lines of inquiry are configured for this campaign.
          </Typography>
        ) : (
          sortedLois.map((loi, index) => (
            <LaunchLoiCard
              key={loi.id}
              loi={loi}
              ordinal={index + 1}
              instrumentsForLoi={instrumentsByLoi.get(loi.id) ?? []}
              canWrite={canWrite}
              campaignEnded={campaignEnded}
              unlockOpen={unlockTargetLoiId === loi.id}
              onUnlockOpen={() => setUnlockTargetLoiId(loi.id)}
              onUnlockClose={() => setUnlockTargetLoiId(null)}
              onConfirmUnlock={confirmUnlock}
              unlockPending={unlockLoi.isPending}
              onCreateInstrument={onCreateInstrument}
              onNavigateInstrumentDashboard={(instId) => onDashboard(instId)}
              onNavigateLoiResponseDashboard={onLoiResponseDashboard}
              onContinueInstrument={(instId) =>
                onContinueInstrument(instId, loi.id)
              }
              onDeleteDraftClick={setDeleteDraftTarget}
              onReactivateClick={handleReactivateClick}
              onPauseClick={setPauseTarget}
              onStopClick={setStopTarget}
              onEditLiveClick={campaignEnded ? undefined : requestLiveEdit}
              onRemindClick={campaignEnded ? undefined : setReminderTarget}
            />
          ))
        )}
      </Box>

      {liveEditDialog}

      <ReactivateInstrumentModal
        open={reactivateTarget != null}
        onClose={() => setReactivateTarget(null)}
        onConfirm={confirmReactivate}
        confirming={reactivateInstrument.isPending || budgetGate.checking}
      />

      <DeleteDraftInstrumentDialog
        open={deleteDraftTarget != null}
        onClose={() => setDeleteDraftTarget(null)}
        onConfirm={() => {
          void confirmDeleteDraft()
        }}
        confirming={deleteDraftInstrument.isPending}
      />

      {reminderTarget != null ? (
        <SendSurveyReminderDialog
          open
          instrumentId={reminderTarget.id}
          onClose={() => setReminderTarget(null)}
        />
      ) : null}

      <PauseInstrumentDialog
        open={pauseTarget != null}
        onClose={() => setPauseTarget(null)}
        onConfirm={confirmPause}
        confirming={pauseInstrument.isPending}
      />

      <StopInstrumentDialog
        open={stopTarget != null}
        instrumentName={stopTarget?.name ?? ''}
        onClose={() => setStopTarget(null)}
        onConfirm={confirmStop}
        confirming={stopInstrument.isPending}
      />

      <InsufficientBudgetModal
        open={budgetGate.insufficientOpen}
        onClose={() => budgetGate.setInsufficientOpen(false)}
        onTopUp={budgetGate.openTopUp}
      />
    </>
  )
}
